// Общие окна: MOD-RESOURCE-INFO, MOD-ENERGY, MOD-REWARD (E06-T09, ux/03 §2, §3, §7), MOD-CONFIRM (ux/11 §5),
// MOD-SESSION-ELSEWHERE ⛔ (ux/11 §3.8), MOD-CONSENT, MOD-TUT-DONE (ux/11 §1.4).
import { useEffect } from 'preact/hooks';
import type { ActionButton } from '../nav/bottom-bar.js';
import { useApp } from '../app/app.js';
import { CURRENCY, amount, duration, num, timer } from '../i18n/format.js';
import { itemName } from '../content/catalog.js';
import { online, wallet } from '../state/game.js';
import type { SheetProps } from '../ui/App.js';
import { Dialog, Sheet, useArmDelay, useNow } from '../ui/kit.js';
import { reboot } from './system.js';
import { ThermosRow } from './store-thermos.js';
import { I, ICONS, type IconName } from '../assets/icons.js';
import { t as tr } from '../i18n/t.js';

const SOURCES: Record<string, { text: string; go?: 'map' | 'shop' }[]> = {
  nuts: [{ text: tr('CL-SCREENS-MODALS-01'), go: 'map' }, { text: tr('CL-SCREENS-MODALS-02') }],
  talons: [{ text: tr('CL-SCREENS-MODALS-03'), go: 'shop' }],
  diesel: [{ text: tr('CL-SCREENS-MODALS-04'), go: 'map' }],
  scrap: [{ text: tr('CL-SCREENS-MODALS-05') }],
  keys: [{ text: tr('CL-SCREENS-MODALS-06') }],
  tickets: [{ text: tr('CL-SCREENS-MODALS-06') }],
};
const SINKS: Record<string, string[]> = {
  nuts: [tr('CL-SCREENS-MODALS-07'), tr('CL-SCREENS-MODALS-08')], talons: [tr('CL-SCREENS-MODALS-09')], diesel: [tr('CL-SCREENS-MODALS-10')],
  scrap: [tr('CL-SCREENS-MODALS-07')], keys: [tr('SHOP-TAB-BOXES')], tickets: [tr('CL-SCREENS-MODALS-11')],
};

export function ResourceInfoSheet({ entry }: SheetProps) {
  const app = useApp();
  const code = String(entry.params['currency'] ?? 'nuts');
  const w = wallet.value;
  const have = w ? (w as unknown as Record<string, number>)[code] ?? 0 : 0;
  const c = CURRENCY[code] ?? { name: code, icon: '•', gen: [code, code, code] as [string, string, string] };
  const actions: ActionButton[] = code === 'talons' && online.value
    ? [{ text: tr('CL-SCREENS-MODALS-12'), testId: 'res-topup', onClick: () => { app.nav.closeSheets(); app.nav.openSheet('SCR-STORE', {}, { guest: true }); } }]
    : [];
  return (
    <Sheet entry={entry} title={<>{code in ICONS ? <I n={code as IconName} size={24} /> : c.icon} {c.name}</>} actions={actions}>
      <div class="col" data-testid={`res-info-${code}`}>
        <div style={{ fontSize: 28, fontWeight: 800 }}>{num(have)}</div>
        <b>{tr('CL-SCREENS-MODALS-13')}</b>
        <div class="list">
          {(SOURCES[code] ?? []).map((s) => (
            <button key={s.text} class="list-row" disabled={!s.go} onClick={() => { if (s.go) { app.nav.closeSheets(); app.nav.selectTab(s.go); } }}>
              <span style={{ flex: 1 }}>{s.text}</span>{s.go ? <span class="accent-text">›</span> : null}
            </button>
          ))}
        </div>
        <b>{tr('CL-SCREENS-MODALS-14')}</b>
        {(SINKS[code] ?? []).map((s) => <span key={s} class="muted">• {s}</span>)}
      </div>
    </Sheet>
  );
}

/** MOD-ENERGY: «+1 через», «полный через» по серверу; термос — с альфы, рекламы в MVP нет (канон §10). */
export function EnergySheet({ entry }: SheetProps) {
  const app = useApp();
  const now = useNow();
  const w = wallet.value;
  const need = entry.params['need'] as number | undefined;
  useEffect(() => { if (need) app.haptic('warning'); }, []);
  const e = w?.energy;
  const nextIn = e?.nextAt ? Date.parse(e.nextAt) - now : 0;
  const fullIn = e?.fullAt ? Date.parse(e.fullAt) - now : 0;
  const ready = !!need && !!e && e.value >= need;
  return (
    <Sheet entry={entry} title={e && e.value < (need ?? 10) ? tr('UI-ENERGY-TITLE') : tr('CL-SCREENS-MODALS-15')} actions={[
      ready
        ? { text: tr('UI-BTN-FIGHT'), testId: 'energy-fight', onClick: () => app.nav.popSheet() }
        : { text: tr('CL-SCREENS-MODALS-16'), tone: 'outline', testId: 'energy-ok', onClick: () => app.nav.popSheet() },
    ]}>
      <div class="col" data-testid="energy">
        <div style={{ fontSize: 36, fontWeight: 800 }} data-testid="energy-value">{e ? `${e.value}/${e.max}` : '—'} <I n="energy" size={32} /></div>
        {need ? <span class={e && e.value < need ? 'neg' : 'pos'}>{tr('CL-SCREENS-MODALS-17')}{" "}{need} {" "}{tr('CL-SCREENS-MODALS-18')}{e ? tr('CL-SCREENS-MODALS-19', { p0: e.value }) : ''}</span> : null}
        {e && e.value < e.max ? (
          <div class="card col" style={{ gap: 4 }}>
            <span data-testid="energy-next">{tr('CL-SCREENS-MODALS-20')}{" "}{timer(nextIn)}</span>
            <span data-testid="energy-full">{tr('CL-SCREENS-MODALS-21')}{" "}{duration(fullIn)}</span>
            <span class="small muted">{tr('CL-SCREENS-MODALS-22')}{" "}{e.max}</span>
          </div>
        ) : <div class="card">{tr('UI-ENERGY-FULL')}</div>}
        <ThermosRow />
        {!online.value ? <span class="small neg">{tr('CL-SCREENS-MODALS-24')}</span> : null}
      </div>
    </Sheet>
  );
}

/** MOD-REWARD: состав награды из ответа сервера; «Забрать» — только подтверждение показа (ux/03 §7). */
export function RewardSheet({ entry }: SheetProps) {
  const app = useApp();
  const g = (entry.params['grant'] ?? {}) as { currencies?: Record<string, number>; items?: { defId: string; qty: number }[] };
  return (
    <Sheet entry={entry} title={tr('CL-SCREENS-MODALS-25')} actions={[{ text: tr('UI-BTN-COLLECT'), testId: 'reward-ok', onClick: () => app.nav.popSheet() }]}>
      <div class="row" style={{ flexWrap: 'wrap', gap: 8 }} data-testid="reward">
        {Object.entries(g.currencies ?? {}).map(([k, v], i) => <span key={k} class="badge soft reward-pop" style={{ fontSize: 15, padding: '8px 12px', '--i': i }}>{k in ICONS ? <I n={k as IconName} size={18} /> : CURRENCY[k]?.icon} {amount(k, v)}</span>)}
        {(g.items ?? []).map((i, n) => <span key={i.defId} class="badge soft reward-pop" style={{ fontSize: 15, padding: '8px 12px', '--i': Object.keys(g.currencies ?? {}).length + n }}>{itemName(i.defId)}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>)}
      </div>
    </Sheet>
  );
}

/** MOD-CONFIRM ⛔: главная — безопасное действие, вторая — рискованное; активна через 1.5 с для эпик/легенд. */
export function ConfirmDialog({ entry }: SheetProps) {
  const app = useApp();
  const p = entry.params as { title?: string; text?: string; risky?: string; safe?: string; onRisky?: () => void; onSafe?: () => void; armMs?: number };
  const armed = useArmDelay(p.armMs ?? 0);
  return (
    <Dialog title={p.title ?? tr('CL-SCREENS-MODALS-26')} text={p.text} testId="MOD-CONFIRM" actions={[
      { text: armed ? p.risky ?? tr('CL-SCREENS-MODALS-27') : tr('CL-SCREENS-MODALS-28'), tone: 'danger', disabled: !armed, testId: 'confirm-risky', onClick: () => { app.nav.closeConfirm(); p.onRisky?.(); } },
      { text: p.safe ?? tr('UI-FIGHT-LEAVE-STAY'), testId: 'confirm-safe', onClick: () => { app.nav.closeConfirm(); p.onSafe?.(); } },
    ]} />
  );
}

export function SessionElsewhereDialog(_p: SheetProps) {
  const app = useApp();
  return (
    <Dialog title={tr('CL-SCREENS-MODALS-29')} text={tr('ERR-SESSION-OTHER')} testId="MOD-SESSION-ELSEWHERE" actions={[
      { text: tr('UI-BTN-CLOSE'), tone: 'outline', onClick: () => app.wa?.close?.() },
      { text: tr('CL-SCREENS-MODALS-30'), testId: 'play-here', onClick: () => { app.nav.closeSheets(); reboot.run(); } },
    ]} />
  );
}

/** CONSENT_REQUIRED: лист «Правила обновились» и «Принимаю» → POST /me/consents (ux/11 §1.1, Т-К-59). */
export function ConsentDialog({ entry }: SheetProps) {
  const app = useApp();
  const d = (entry.params['details'] ?? {}) as { docId?: 'terms' | 'privacy' | 'chat_rules'; version?: string };
  return (
    <Dialog title={tr('CL-SCREENS-MODALS-31')} text={tr('CL-SCREENS-MODALS-32')} testId="MOD-CONSENT" actions={[
      { text: tr('CL-SCREENS-MODALS-33'), onClick: async () => {
        try { await app.call('acceptConsent', { body: { docId: d.docId ?? 'terms', version: d.version ?? 'latest' } }); app.nav.closeSheets(); } catch (e) { app.handleError(e); }
      } },
    ]} />
  );
}

export function TutDoneDialog(_p: SheetProps) {
  const app = useApp();
  return (
    <Dialog title={tr('TUT-T10-08')} testId="MOD-TUT-DONE" text={tr('TUT-T10-09')} actions={[
      { text: tr('TUT-T10-10'), testId: 'tut-done', onClick: () => { app.nav.closeSheets(); app.nav.selectTab('home'); } },
    ]}>
      <div class="col small" style={{ marginBottom: 12 }}>
        <span>{tr('CL-SCREENS-MODALS-37')}</span>
        <span>{tr('CL-SCREENS-MODALS-38')}</span>
        <span>{tr('CL-SCREENS-MODALS-39')}</span>
      </div>
    </Dialog>
  );
}
